import BoldSmallText from '@/components/BoldSmallText';
import BoldText from '@/components/BoldText';
import { CircleCheckBox } from '@/components/CircleCheckBox';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { WIDGET_SETTING_CHOICE } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { BoldTextDiv, ButtonStyled, FullHeightDiv } from '@/pages/Dashboard/styled';
import { useUpdateSetupGuideMutation } from '@/redux/query';
import { handleLocationTab } from '@/redux/slice/tab.slice';
import { Badge, BlockStack, Box, Button, Card, Collapsible, Divider, InlineStack, Link } from '@shopify/polaris';
import { ChevronRightIcon, XIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  checkedStep: string[];
  isActiveEmbed?: boolean;
  themeId?: string;
  isEnableMethod?: boolean;
}

export const RequireActionCard = ({ isOpen, onClose, checkedStep, isActiveEmbed, themeId, isEnableMethod }: Props) => {
  const { t } = useTranslation('dashboard');
  const [dismissed, setDismissed] = useState(isOpen);
  const navigate = useNavigate();
  const [updateSetupGuide] = useUpdateSetupGuideMutation();
  const dispatch = useDispatch();
  const appEmbedId = process.env.REACT_APP_SHOPIFY_THEME_APP_EXTENSION_ID;
  const [checkedCircle, setCheckedCircle] = useState<string[]>(checkedStep);

  const checkStep = useCallback(
    async (step: string) => {
      if (checkedCircle.includes(step)) {
        return;
      }

      const nextChecked = [...checkedCircle, step];
      setCheckedCircle(nextChecked);

      try {
        await updateSetupGuide({
          shop: config.shop,
          checkedSetupStep: nextChecked.filter((item) => item !== '').map(Number),
        }).unwrap();
      } catch (err) {
        console.error('updateSetupGuide failed:', err);
      }
    },
    [checkedCircle, updateSetupGuide],
  );

  const handleNavigate = useCallback(
    async (path: { pathname: string; search: string }, step: string) => {
      await checkStep(step);
      navigate(path);
    },
    [checkStep, navigate],
  );

  const handleEmbedApp = useCallback(() => {
    if (!appEmbedId) {
      console.log('test');
      return;
    }

    const appIdPath = encodeURIComponent(`${appEmbedId}/app-embed-block.`);
    const url = new URL(`https://${config.shop}/admin/themes/${themeId}/editor`);
    url.searchParams.set('context', 'apps');
    url.searchParams.set('template', 'cart');
    url.searchParams.set('activateAppId', appIdPath);

    window.open(decodeURIComponent(url.toString()), '_blank');
  }, [appEmbedId, themeId]);

  useEffect(() => {
    setCheckedCircle(checkedStep);
  }, [checkedStep]);

  const requireSetupData = useMemo(
    () => [
      {
        id: 1,
        isChecked: isActiveEmbed,
        label: (
          <BlockStack align="start" inlineAlign="start" gap={'100'}>
            <BoldTextDiv onClick={handleEmbedApp}>
              <Link>{t(`requireCard.step.1`)}</Link>
            </BoldTextDiv>
            <RegularText>
              Please embed app to show widget on your store. You can try in your{' '}
              <Button variant="plain" onClick={() => window.open('https://docs.dingdoong.io/how-to-create-a-test-theme')}>
                test theme
              </Button>{' '}
              before live.{' '}
            </RegularText>
          </BlockStack>
        ),
      },
      {
        id: 2,
        isChecked: isEnableMethod,
        label: (
          <BlockStack align="start" inlineAlign="start" gap={'100'}>
            <BoldSmallText>{t('requireCard.step.2')}</BoldSmallText>
            <InlineStack gap={'200'}>
              <ButtonStyled>
                <Button size="large" icon={ChevronRightIcon} onClick={() => navigate(PATH.LOCATIONS)}>
                  {t('requireCard.schedule.deliveryPickup')}
                </Button>
              </ButtonStyled>
              <ButtonStyled>
                <Button size="large" variant="primary" icon={ChevronRightIcon} onClick={() => navigate(PATH.SHIPPING)}>
                  {t('requireCard.schedule.shipping')}
                </Button>
              </ButtonStyled>
            </InlineStack>
          </BlockStack>
        ),
      },
      {
        id: 3,
        isChecked: checkedCircle.includes('3'),
        label: (
          <BlockStack align="start" inlineAlign="start" gap={'100'}>
            <BoldTextDiv onClick={(e) => e.stopPropagation()}>
              <Link
                onClick={() => {
                  checkStep('3');
                  dispatch(handleLocationTab({ key: 'widgetTab', value: WIDGET_SETTING_CHOICE.DESIGN }));
                  handleNavigate(PATH.WIDGET, '3');
                }}
              >
                {t(`requireCard.step.3`)}
              </Link>
            </BoldTextDiv>
          </BlockStack>
        ),
      },
      {
        id: 4,
        isChecked: checkedCircle.includes('4'),
        label: (
          <BlockStack align="start" inlineAlign="start" gap={'100'}>
            <BoldSmallText>{t('requireCard.step.4')}</BoldSmallText>
            <ButtonStyled>
              <Button size="large" variant="primary" icon={ChevronRightIcon} onClick={() => handleNavigate(PATH.PRICING, '4')}>
                {t('requireCard.pricing')}
              </Button>
            </ButtonStyled>
          </BlockStack>
        ),
      },
    ],
    [checkStep, checkedCircle, dispatch, handleEmbedApp, handleNavigate, isActiveEmbed, isEnableMethod, navigate, t],
  );

  return (
    <FullHeightDiv>
      <Collapsible id={'cta-widget-banner'} open={dismissed}>
        <Card padding={'0'}>
          <Box padding={'0'} position="relative">
            <Box position="absolute" insetBlockStart="0" insetInlineEnd="0" padding={'300'}>
              <Button
                icon={XIcon}
                variant="tertiary"
                onClick={() => {
                  onClose();
                  setDismissed(false);
                }}
              />
            </Box>
            <FullHeightDiv>
              <BlockStack>
                <div className="not-full-inline not-fullHeight-blockStack">
                  <Box padding={'300'}>
                    <BlockStack gap="150">
                      <InlineStack gap={'200'}>
                        <BoldText>{t('requireCard.title')}</BoldText>
                        <BoldTextDiv>
                          <Badge tone="critical">{t('requireCard.badge')}</Badge>
                        </BoldTextDiv>
                      </InlineStack>
                      <RegularText tone="subdued">{t('requireCard.desc')}</RegularText>
                    </BlockStack>
                  </Box>
                  <Divider borderColor="border" />
                </div>
                <Box padding={'200'}>
                  <BlockStack gap={'300'} align="space-between">
                    {requireSetupData.map((item) => (
                      <Card key={item.id}>
                        <CircleCheckBox key={item.id} label={item.label} checked={item.isChecked} />
                      </Card>
                    ))}
                  </BlockStack>
                </Box>
              </BlockStack>
            </FullHeightDiv>
          </Box>
        </Card>
      </Collapsible>
    </FullHeightDiv>
  );
};
